import { parseRunnerSearchParams } from "./runner";
import { postGameResize, postRunnerStatus, type RunnerStatus } from "./messages";
import type { GameMeta, GameMountContext, GameMountHandle } from "./types";

export interface StandaloneModule {
  mount?: (
    root: HTMLElement,
    ctx?: GameMountContext
  ) => Promise<GameMountHandle | void> | GameMountHandle | void;
  meta?: GameMeta;
}

export type StandaloneModuleImporter = () => Promise<StandaloneModule>;

export interface GameLoader {
  resolveEntry(
    gameId: string
  ):
    | Promise<StandaloneModule | StandaloneModuleImporter | undefined>
    | StandaloneModule
    | StandaloneModuleImporter
    | undefined;
}

export interface RunnerDomRefs {
  root: HTMLElement;
  loader?: HTMLElement | null;
  loaderText?: HTMLElement | null;
}

export interface RunnerOptions {
  /**
   * Provide DOM references so the runner can show/hide its loader and render errors.
   * Defaults to looking up #root, #runner-loader, and #runner-loader-text.
   */
  dom?: RunnerDomRefs | (() => RunnerDomRefs);
  /**
   * Optional callback for status transitions (useful for logging).
   */
  onStatus?: (status: RunnerStatus, message?: string) => void;
  /**
   * Whether to treat the environment as "dev" for retry behaviour.
   * Pass `import.meta.env.DEV` from the host to avoid relying on bundler globals here.
   */
  isDev?: boolean;
  /**
   * Milliseconds between retries when an import fails in dev.
   * Defaults to 400ms.
   */
  retryDelayMs?: number;
  /**
   * Maximum number of retry attempts in dev when the standalone bundle is still building.
   * Defaults to 4 (roughly 2 seconds total).
   */
  maxRetries?: number;
  /**
   * Fallback to build a document title when the module has no meta.title.
   */
  fallbackTitle?: (gameId: string) => string;
}

function resolveDomRefs(dom?: RunnerDomRefs | (() => RunnerDomRefs)): RunnerDomRefs {
  if (typeof dom === "function") return dom();
  if (dom) return dom;

  const root = document.getElementById("root");
  if (!root) {
    throw new Error("Missing #root container for game runner");
  }
  return {
    root,
    loader: document.getElementById("runner-loader"),
    loaderText: document.getElementById("runner-loader-text"),
  };
}

function showLoader(refs: RunnerDomRefs, message = "Loading…") {
  if (refs.loaderText) {
    refs.loaderText.textContent = message;
  }
  if (refs.loader) {
    refs.loader.style.display = "flex";
  }
}

function hideLoader(refs: RunnerDomRefs) {
  if (refs.loader) {
    refs.loader.style.display = "none";
  }
}

function renderMessage(refs: RunnerDomRefs, message: string) {
  const paragraph = document.createElement("p");
  paragraph.style.fontSize = "1rem";
  paragraph.style.padding = "1.5rem";
  paragraph.style.textAlign = "center";
  paragraph.textContent = message;
  hideLoader(refs);

  refs.root.innerHTML = "";
  const wrapper = document.createElement("div");
  wrapper.style.display = "flex";
  wrapper.style.alignItems = "center";
  wrapper.style.justifyContent = "center";
  wrapper.style.minHeight = "100%";
  wrapper.style.width = "100%";
  wrapper.style.boxSizing = "border-box";
  wrapper.appendChild(paragraph);
  refs.root.appendChild(wrapper);
}

async function importWithRetry(
  loader: () => Promise<StandaloneModule>,
  options: { isDev?: boolean; maxRetries: number; retryDelayMs: number },
  attempt = 0
): Promise<StandaloneModule> {
  try {
    return await loader();
  } catch (error) {
    const shouldRetry = options.isDev && attempt < options.maxRetries;
    if (!shouldRetry) {
      throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, options.retryDelayMs));
    return importWithRetry(loader, options, attempt + 1);
  }
}

function startAutoHeight(
  refs: RunnerDomRefs,
  gameId: string,
  autoHeight: boolean | undefined,
  origin: string
): () => void {
  if (!autoHeight || window.parent === window) {
    return () => undefined;
  }

  let rafId: number | undefined;
  const sendHeight = (height: number) => {
    postGameResize(window.parent, { type: "game-resize", gameId, height }, origin);
  };

  const measureAndSend = () => {
    rafId = undefined;
    const scrollHeight = refs.root.scrollHeight;
    const offsetHeight = refs.root.offsetHeight;
    const clientHeight = refs.root.clientHeight;
    const height = Math.max(scrollHeight, offsetHeight, clientHeight);
    if (height > 0) {
      sendHeight(height);
    }
  };

  const resizeObserver = new ResizeObserver(() => {
    if (rafId !== undefined) return;
    rafId = window.requestAnimationFrame(measureAndSend);
  });

  resizeObserver.observe(refs.root);
  measureAndSend();

  return () => {
    resizeObserver.disconnect();
    if (rafId !== undefined) {
      cancelAnimationFrame(rafId);
    }
  };
}

export async function bootIframeRunner(
  loader: GameLoader,
  options: RunnerOptions = {}
): Promise<(() => void) | undefined> {
  const refs = resolveDomRefs(options.dom);
  const origin = window.location.origin;
  const retryDelayMs = options.retryDelayMs ?? 400;
  const maxRetries = options.maxRetries ?? 4;
  const isDev = options.isDev ?? false;

  const params = new URLSearchParams(window.location.search);
  const { gameId, context, autoHeight } = parseRunnerSearchParams(params);

  if (!gameId) {
    renderMessage(refs, "Missing game identifier in query string (use ?game=amped-up)");
    postRunnerStatus(window.parent, { type: "runner-status", status: "error", message: "missing-game-id" }, origin);
    options.onStatus?.("error", "missing-game-id");
    return undefined;
  }

  let resolvedModule: StandaloneModule | undefined;
  try {
    const standalone = await loader.resolveEntry(gameId);
    if (!standalone) {
      renderMessage(refs, `Game "${gameId}" not found.`);
      postRunnerStatus(
        window.parent,
        { type: "runner-status", status: "error", gameId, message: "game-not-found" },
        origin
      );
      options.onStatus?.("error", "game-not-found");
      return undefined;
    }

    if (typeof standalone === "function") {
      resolvedModule = await importWithRetry(standalone as StandaloneModuleImporter, {
        isDev,
        maxRetries,
        retryDelayMs,
      });
    } else {
      resolvedModule = standalone;
    }
  } catch (error) {
    console.error("[game-runner] Failed to resolve module", error);
    renderMessage(refs, `Failed to load game "${gameId}": ${(error as Error)?.message ?? "Unknown error"}`);
    postRunnerStatus(
      window.parent,
      { type: "runner-status", status: "error", gameId, message: (error as Error)?.message },
      origin
    );
    options.onStatus?.("error", (error as Error)?.message);
    return undefined;
  }

  showLoader(refs, `Loading ${gameId}…`);
  postRunnerStatus(window.parent, { type: "runner-status", status: "loading", gameId }, origin);
  options.onStatus?.("loading");

  try {
    if (typeof resolvedModule?.mount !== "function") {
      renderMessage(refs, `Game "${gameId}" is missing a mount() export.`);
      postRunnerStatus(
        window.parent,
        { type: "runner-status", status: "error", gameId, message: "missing-mount" },
        origin
      );
      options.onStatus?.("error", "missing-mount");
      return undefined;
    }

    const ctx: GameMountContext = {
      ...context,
      locale: context.locale ?? "en",
    };

    refs.root.innerHTML = "";
    const handle = await resolvedModule.mount(refs.root, ctx);

    const stopAutoHeight = startAutoHeight(refs, gameId, autoHeight, origin);

    if (resolvedModule.meta?.title) {
      document.title = resolvedModule.meta.title;
    } else if (options.fallbackTitle) {
      document.title = options.fallbackTitle(gameId);
    }

    hideLoader(refs);
    postRunnerStatus(window.parent, { type: "runner-status", status: "ready", gameId }, origin);
    options.onStatus?.("ready");

    const destroy = () => {
      stopAutoHeight();
      if (handle && typeof handle === "object" && typeof handle.destroy === "function") {
        handle.destroy();
      }
    };

    return destroy;
  } catch (error) {
    console.error(`[game-runner] Failed to boot game "${gameId}"`, error);
    renderMessage(refs, `Failed to load game "${gameId}": ${(error as Error)?.message ?? "Unknown error"}`);
    postRunnerStatus(
      window.parent,
      { type: "runner-status", status: "error", gameId, message: (error as Error)?.message },
      origin
    );
    options.onStatus?.("error", (error as Error)?.message);
    return undefined;
  }
}
